import { apiClient } from '../../api/client';
import type { components } from '../../api/generated/schema';
import type {
  ListingChanges,
  ListingStatusAction,
  MyListingsShelf,
  NewListing,
} from '../../domain/listings';
import { mapped } from '../errors';
import type { ListingsRepository } from './ListingsRepository';
import {
  toCursorPage,
  toListing,
  toListingChanges,
  toListingPage,
  toManifestEntry,
  toNewListing,
  toPhotos,
  toStatusAction,
} from './mapping';

type S = components['schemas'];

const PUBLISH_STATE: Record<MyListingsShelf, string> = { draft: 'draft', notDraft: 'not_draft' };

const path = (id: string) => `/api/v1/listings/${encodeURIComponent(id)}/`;

function listingsQuery(params: Record<string, string>, page: number): string {
  const query = new URLSearchParams(params);
  if (page > 1) query.set('page', String(page));
  return `/api/v1/listings/?${query}`;
}

export const httpListingsRepository: ListingsRepository = {
  inCollection: (collectionId: string, page = 1) =>
    mapped(async () =>
      toListingPage(
        await apiClient.get<S['PaginatedListingList']>(
          listingsQuery({ collection: collectionId }, page),
        ),
      ),
    ),
  mine: (page = 1, shelf?: MyListingsShelf) =>
    mapped(async () =>
      toListingPage(
        await apiClient.get<S['PaginatedListingList']>(
          listingsQuery(
            shelf ? { author: 'me', publish_state: PUBLISH_STATE[shelf] } : { author: 'me' },
            page,
          ),
        ),
      ),
    ),
  get: (id: string) => mapped(async () => toListing(await apiClient.listingGet(id))),
  create: (input: NewListing) =>
    mapped(async () => toListing(await apiClient.listingCreate(toNewListing(input)))),
  update: (id: string, changes: ListingChanges) =>
    mapped(async () =>
      toListing(await apiClient.send<S['Listing']>('PATCH', path(id), toListingChanges(changes))),
    ),
  delete: (id: string) =>
    mapped(async () => {
      await apiClient.send('DELETE', path(id));
    }),
  changeStatus: (id: string, action: ListingStatusAction, publishAt?: string | null) =>
    mapped(async () => {
      const body: S['ListingStatusChangeRequest'] = { action: toStatusAction(action) };
      if (publishAt !== undefined) body.publish_at = publishAt;
      return toListing(await apiClient.send<S['Listing']>('POST', `${path(id)}status/`, body));
    }),
  clone: (id: string, collectionId: string) =>
    mapped(async () =>
      toListing(
        await apiClient.send<S['Listing']>('POST', `${path(id)}clone/`, {
          collection_id: collectionId,
        }),
      ),
    ),
  addPhotos: (id: string, pendingPhotoIds: string[]) =>
    mapped(async () =>
      toPhotos(
        await apiClient.send<S['Photo'][]>('POST', `${path(id)}photos/`, {
          pending_image_ids: pendingPhotoIds,
        }),
      ),
    ),
  reorderPhotos: (id: string, photoIds: string[]) =>
    mapped(async () =>
      toPhotos(
        await apiClient.send<S['Photo'][]>('PUT', `${path(id)}photos/order/`, {
          photo_ids: photoIds,
        }),
      ),
    ),
  deletePhoto: (id: string, photoId: string) =>
    mapped(async () => {
      await apiClient.send('DELETE', `${path(id)}photos/${encodeURIComponent(photoId)}/`);
    }),
  feed: (cursor?: string | null) =>
    mapped(async () => {
      const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
      return toCursorPage(await apiClient.get<S['ListingCursorPage']>(`/api/v1/feed/${query}`));
    }),
  syncManifest: () =>
    mapped(async () =>
      (await apiClient.get<S['ListingManifestEntry'][]>('/api/v1/sync/listings/')).map(
        toManifestEntry,
      ),
    ),
};
